import { useState } from "react";
import { toast } from "sonner";
import { GripVertical, Mail, Star } from "lucide-react";
import { adminApi } from "@/lib/api";
import { CRM_STATUSES } from "./CrmControls";

const KanbanCard = ({ l, onEmail, onCrm }) => (
  <div data-testid={`kanban-card-${l.id}`} draggable
    onDragStart={(e) => { e.dataTransfer.setData("text/plain", l.id); e.dataTransfer.effectAllowed = "move"; }}
    className="group bg-[#f4f6f7] border border-[#d5dbde] hover:border-[#52525b] p-3 cursor-grab active:cursor-grabbing transition-colors">
    <div className="flex items-start gap-2">
      <GripVertical className="w-3.5 h-3.5 text-[#52525b] mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#2B3036] truncate">{l.company_name}</div>
        <div className="text-[11px] font-mono text-[#6b7280] truncate">{l.city} · {l.country}</div>
        {l.crm_note && <div className="text-[11px] text-[#f59e0b] mt-1 line-clamp-2">{l.crm_note}</div>}
        <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-[#6b7280]">
          {l.reviews_rating && <span className="flex items-center gap-0.5 text-[#f59e0b]"><Star className="w-2.5 h-2.5 fill-current" />{l.reviews_rating}</span>}
          {l.last_contact_at && <span>ult. {new Date(l.last_contact_at).toLocaleDateString("it-IT")}</span>}
        </div>
      </div>
    </div>
    <div className="flex gap-1.5 mt-2 opacity-60 group-hover:opacity-100 transition-opacity">
      <button data-testid={`kanban-note-${l.id}`} onClick={() => onCrm(l)}
        className="text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] px-2 py-1 text-[#5b6168]">Nota</button>
      <button data-testid={`kanban-email-${l.id}`} onClick={() => onEmail([l])} disabled={!l.email}
        className="text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] px-2 py-1 text-[#5b6168] disabled:opacity-30 inline-flex items-center gap-1">
        <Mail className="w-2.5 h-2.5" /> Email
      </button>
    </div>
  </div>
);

const KanbanColumn = ({ s, items, onDrop, onEmail, onCrm }) => {
  const [over, setOver] = useState(false);
  return (
    <div data-testid={`kanban-column-${s.id}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain"), s.id); }}
      className="bg-white border flex flex-col min-h-[420px] transition-colors"
      style={{ borderColor: over ? s.color : "#27272a", background: over ? `${s.color}0d` : undefined }}>
      <div className="px-4 py-3 border-b border-[#d5dbde] flex items-center justify-between" style={{ boxShadow: `inset 0 2px 0 ${s.color}` }}>
        <span className="text-[10px] uppercase tracking-widest font-mono" style={{ color: s.color }}>{s.label}</span>
        <span className="font-display text-lg text-[#2B3036]">{items.length}</span>
      </div>
      <div className="p-2 space-y-2 overflow-y-auto max-h-[560px] flex-1">
        {items.map((l) => <KanbanCard key={l.id} l={l} onEmail={onEmail} onCrm={onCrm} />)}
        {!items.length && <div className="text-center text-[11px] font-mono text-[#52525b] py-10">Trascina qui</div>}
      </div>
    </div>
  );
};

export const KanbanBoard = ({ leads, onUpdated, onEmail, onCrm }) => {
  const move = async (id, status) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead || (lead.crm_status || "nuovo") === status) return;
    onUpdated({ ...lead, crm_status: status });
    try {
      onUpdated(await adminApi.updateCrm(id, { crm_status: status }));
      toast.success(`${lead.company_name} → ${CRM_STATUSES.find((s) => s.id === status).label}`);
    } catch {
      onUpdated(lead);
      toast.error("Errore spostamento lead");
    }
  };
  return (
    <div data-testid="kanban-board" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {CRM_STATUSES.map((s) => (
        <KanbanColumn key={s.id} s={s} items={leads.filter((l) => (l.crm_status || "nuovo") === s.id)}
          onDrop={move} onEmail={onEmail} onCrm={onCrm} />
      ))}
    </div>
  );
};
